import React, { useState } from 'react';
import {
  Alert, Box, Typography, Link,
} from '@mui/material';
import XLSXform8PlacedWastesExample from '../../../../../assets/files/KO_template_import_wastes_objects.xlsx';
import { Text, TextTitle } from '../../../../Text';
import DeleteButton from '../../../../Buttons/DeleteButton';
import AddButton from '../../../../Buttons/AddButton';
import { FORM8_PLACING_OBJECTS_NAME, getInitialPlacedWaste, PLACED_WASTES_NAME } from '../../../../../constants/lkCadastr/cadastrForm8';
import {
  fitContent, flexGap2, flexRowGap2, fullWidth,
} from '../../../../../constants/lkCadastr/cadastrStorage';
import CustomInput from '../../../../Inputs';
import { AsyncAutocomplete } from '../../../../AutoCompletes/AsyncAutocomplete';
import { getWasteTypesRequest } from '../../../../../api/wasteTypeApi';
import { DEFAULT_AUTOCOMPLETE_FIELD } from '../../../../Inputs/CustomAutocomplete';
import useCadastreTabs from '../../../../../hooks/useCadastreTabs';
import NavTabs from '../../../../Tabs';
import { deleteForm8Entity, getReportPlacingWasteHelper } from '../../../../../helpers/cadastreForms/form8Helper';
import { compareId } from '../../../../../helpers';
import CustomButton from '../../../../Buttons/CustomButton';
import ModalWithTitle from '../../../ModalWithTitle';
import DropZone from '../../../../File/DropZone';
import { useApi } from '../../../../../hooks';
import BackdropLoading from '../../../../BackdropLoading';
import { METHODS_API } from '../../../../../api/api';
import { cadastreReportHistoryRequest, cadastreReportTechnologiesImportRequest } from '../../../../../api/cadastreApi';

export function Form8PlacedWastes({
  placingObject, changeObject, isDemo, openDeleteModal, reportId, changeItemFormByField,
}) {
  const placedWastes = placingObject[PLACED_WASTES_NAME];
  const [openedImport, setOpenedImport] = useState(false);

  const openImportModal = () => setOpenedImport(true);
  const closeImportModal = () => setOpenedImport(false);

  const getFiles = async (dropZoneFiles) => {
    const formData = new FormData();

    formData.append('import[file]', dropZoneFiles[0]);
    formData.append('import[format_type]', 'xlsx');
    formData.append('import[kind]', 'report_object_waste');

    await cadastreReportTechnologiesImportRequest({
      method: METHODS_API.POST,
      data: formData,
      rest: `?report_history_id=${reportId}&placed_object_id=${placingObject?.id}`,
      isFormData: true,
    });

    const newReport = await cadastreReportHistoryRequest({
      method: METHODS_API.GET,
      id: reportId,
    });

    const reportPlacingWaste = getReportPlacingWasteHelper(newReport);

    changeItemFormByField({
      field: `form8.${FORM8_PLACING_OBJECTS_NAME}`, value: [...reportPlacingWaste.placing_objects], type: 'import',
    });

    closeImportModal();
  };

  const { defaultRequest: importFromFile, isLoading: loadingImport } = useApi({ request: getFiles });

  const { tab, setTab, tabs } = useCadastreTabs({ items: placedWastes, tabName: 'Размещённый отход' });

  return (
    <Box sx={flexGap2}>
      <Box sx={flexRowGap2}>
        {!isDemo && (
          <AddButton
            variant="outlined"
            onClick={() => changeObject(
              PLACED_WASTES_NAME,
              [...placedWastes, getInitialPlacedWaste()],
            )}
          >
            Добавить размещённый отход
          </AddButton>
        )}
      </Box>
      <BackdropLoading position="fixed" isLoading={loadingImport} />
      {!isDemo && <CustomButton onClick={openImportModal} disabled={!placingObject?.id}>Импортировать из файла</CustomButton>}
      {!isDemo && !placingObject?.id && (
        <Typography>* Для импорта сохраните форму</Typography>
      )}
      {openedImport && (
        <ModalWithTitle open close={closeImportModal} title="Импортировать из файла">
          <Alert severity="warning" sx={{ mb: 2 }}>
            Загруженный файл должен быть формате Office Excel до 20 Мб, содержать не более 1000 строк. В файле должны содержаться наименование, код, количество, технология размещения и способ размещения отходов.
            <Link sx={{ ml: 1 }} href={XLSXform8PlacedWastesExample}>Скачать пример файла импорта</Link>
          </Alert>
          <DropZone sx={{ height: 120 }} getFiles={(e, files) => importFromFile(files)} maxFiles={1} />
        </ModalWithTitle>
      )}
      <NavTabs tab={tab} setTab={setTab} tabsComponents={tabs} />
      {placedWastes.map((placedWaste, placedWasteIndex) => {
        if (tab !== placedWaste.randId) return null;

        const onChange = (field, value) => {
          changeObject(`${PLACED_WASTES_NAME}.${placedWasteIndex}.${field}`, value, undefined, { placedWasteIndex });
        };

        const onDelete = async () => {
          if (placedWaste.id) {
            await deleteForm8Entity({ id: placedWaste.id, apiType: PLACED_WASTES_NAME });
          }

          const cloneArray = placedWastes.slice();
          cloneArray.splice(placedWasteIndex, 1);

          changeObject(
            PLACED_WASTES_NAME,
            cloneArray,
            'delete',
          );
        };

        const isZeroWaste = compareId(placedWaste.waste_type_id?.code, 0);

        return (
          <Box key={placedWaste.randId} sx={flexGap2}>
            <Box display="flex" alignItems="center" mb={1} gap={3}>
              <TextTitle>
                Информация
                №
                {placedWasteIndex + 1}
              </TextTitle>
              {!isDemo && placedWastes.length > 1 && (
                <DeleteButton
                  onClick={() => openDeleteModal(onDelete, placedWasteIndex, 'Размещённый отход')}
                >
                  Удалить размещённый отход
                </DeleteButton>
              )}
            </Box>
            <Box sx={flexGap2}>
              <Box>
                <Text className="mute">Код отхода по ФККО</Text>
                <AsyncAutocomplete
                  canBeZero
                  validImmediately={!isZeroWaste}
                  debounceTime={0}
                  disabled={isDemo}
                  sx={fullWidth}
                  request={getWasteTypesRequest}
                  value={placedWaste.waste_type_id}
                  optionField={{ ...DEFAULT_AUTOCOMPLETE_FIELD, label: 'code' }}
                  onChange={(e, value) => onChange('waste_type_id', value)}
                />
              </Box>
              <Box>
                <Text className="mute">Наименование размещаемых отходов по ФККО</Text>
                <AsyncAutocomplete
                  canBeZero
                  validImmediately={!compareId(placedWaste.waste_type_id?.name, 0) && !isZeroWaste}
                  debounceTime={0}
                  disabled={isDemo || isZeroWaste}
                  sx={fullWidth}
                  request={getWasteTypesRequest}
                  value={placedWaste.waste_type_id}
                  optionField={{ ...DEFAULT_AUTOCOMPLETE_FIELD, label: 'name' }}
                  onChange={(e, value) => onChange('waste_type_id', value)}
                />
              </Box>
              <Box>
                <Text className="mute">Количество, т</Text>
                <CustomInput
                  debounceTime={0}
                  validImmediately
                  disabled={isDemo}
                  type="number"
                  sx={fitContent}
                  value={placedWaste.tons_per_year}
                  onChange={(e, value) => onChange('tons_per_year', value)}
                />
              </Box>
              <Box>
                <Text className="mute">Технология размещения отходов</Text>
                <CustomInput
                  debounceTime={0}
                  validImmediately
                  disabled={isDemo}
                  value={placedWaste.placement_technology}
                  onChange={(e, value) => onChange('placement_technology', value)}
                  sx={fullWidth}
                />
              </Box>
              <Box>
                <Text className="mute">Способ размещения отходов</Text>
                <CustomInput
                  debounceTime={0}
                  validImmediately
                  disabled={isDemo}
                  value={placedWaste.placement_method}
                  onChange={(e, value) => onChange('placement_method', value)}
                  sx={fullWidth}
                />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
